import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { assignFormValues } from "../../../Utils/Auth";
import {
  computeDisabledPanelIds,
  getConflictingPanelNameForCombination,
  getICDPanelsIndex,
  hideIcdAndComorbidityForPanel,
  panelsArrItemRemoval
} from "../../../Utils/Common/Requisition";
import useLang from "../../hooks/useLanguage";

// Helper function to convert YYYY-MM-DD to MM/DD/YYYY
const formatDateToMMDDYYYY = (dateString: string): string => {
  if (!dateString) return '';
  // Check if already in MM/DD/YYYY format
  if (dateString.includes('/')) {
    return dateString;
  }
  // Convert from YYYY-MM-DD to MM/DD/YYYY
  const [year, month, day] = dateString.split('-');
  if (year && month && day) {
    return `${month}/${day}/${year}`;
  }
  return dateString;
};

// Helper function to convert MM/DD/YYYY to YYYY-MM-DD (for HTML date input)
const formatDateToYYYYMMDD = (dateString: string): string => {
  if (!dateString) return '';
  // Check if already in YYYY-MM-DD format
  if (dateString.includes('-') && dateString.length === 10) {
    return dateString;
  }
  // Convert from MM/DD/YYYY to YYYY-MM-DD
  const parts = dateString.split('/');
  if (parts.length === 3) {
    const [month, day, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return dateString;
};

enum PanelsTypes {
  ListOnly = "List Only",
  Search = "Search",
  SelectableList = "Selectable List",
  SelectableListForTest = "Selectable List For Tests Only",
}

const MultipleHeaderSelectable2 = (props: any) => {
  const { t } = useLang();
  const [panelsArrToSend, setPanelsArrToSend] = useState<any>([]);
  const [duplicateTestErrorsByPanelId, setDuplicateTestErrorsByPanelId] =
    useState<Record<string, string>>({});
  const [openAOESections, setOpenAOESections] = useState<{
    [key: string]: boolean;
  }>({});
  const [mutualExclusivityError, setMutualExclusivityError] = useState<string>("");
  const location = useLocation();

  const currentPanels =
    props?.Inputs?.[props?.index]?.fields?.[props?.fieldIndex]?.panels ??
    props?.panels ??
    [];

  const panelSelectionKey = currentPanels
    .map((p: any) => `${p.panelID}:${p.isSelected}`)
    .join("|");

  const disabledPanelIds = useMemo(
    () => computeDisabledPanelIds(currentPanels, props?.panelCombinations),
    [props?.panelCombinations, panelSelectionKey]
  );

  const getConflictingPanelName = (panelID: string | number): string =>
    getConflictingPanelNameForCombination(
      panelID,
      props?.Inputs?.[props?.index]?.fields?.[props?.fieldIndex]?.panels ??
        props?.panels ??
        [],
      props?.panelCombinations
    );
  useEffect(() => {
    const {
      Inputs,
      index,
      fieldIndex,
      depControlIndex,
      isDependency,
      repeatFieldSection,
      isDependencyRepeatFields,
      repeatFieldIndex,
      repeatDependencySectionIndex,
      repeatDepFieldIndex,
      setInputs,
      infectiousData,
      ArrayReqId,
      setInfectiousData,
    } = props;

    const currentField = Inputs?.[index]?.fields?.[fieldIndex];

    if (currentField?.panels && !location.state?.reqId) {
      currentField.panels.forEach((panel: any) => {
        if (panel.isDefaultSelectOnRequisition) {
          panel.isSelected = true;
          panel.testOptions?.forEach((option: any) => {
            option.isSelected = true;
          });
        }
      });
    }

    // Only the selected panels may go into `defaultValue`. Writing the full
    // `panels` list here left a non-empty value behind with nothing selected,
    // which silently satisfied the field's `min(1)` validation.
    const newInputsPromise = assignFormValues(
      Inputs,
      index,
      depControlIndex,
      fieldIndex,
      (currentField?.panels ?? []).filter((panel: any) => panel?.isSelected),
      isDependency,
      repeatFieldSection,
      isDependencyRepeatFields,
      repeatFieldIndex,
      repeatDependencySectionIndex,
      repeatDepFieldIndex,
      undefined,
      setInputs
    );

    newInputsPromise?.then((updatedSections) => {
      if (!updatedSections) return;

      const infectiousDataCopy = JSON.parse(JSON.stringify(infectiousData));
      const sectionIndex = FindIndex(infectiousData, ArrayReqId);

      if (sectionIndex !== -1) {
        infectiousDataCopy[sectionIndex].sections = updatedSections;
        setInfectiousData([...infectiousDataCopy]);
      }
    });
  }, [props.IsSelectedByDefaultCompendiumData]);

  useEffect(() => {
    setpanelsArrToSendForEdit();
  }, [props.panels]);

  useEffect(() => {
    setMutualExclusivityError("");
  }, [panelSelectionKey]);

  function FindIndex(arr: any[], rid: any) {
    return arr.findIndex((i: any) => i.reqId === rid);
  }

  // Show/hide a panel's ICD-10 group, matched on panelID.
  // Must be applied to the same inputs object that is passed to assignFormValues,
  // otherwise the change is dropped when that object is committed to state.
  const setIcdPanelVisibility = (
    inputs: any,
    icdPanelIndex: any,
    panelID: any,
    isVisible: boolean
  ) => {
    if (icdPanelIndex === undefined || icdPanelIndex === null) return;
    if (!isVisible) {
      // Hiding the card is not enough: any code already picked for this panel
      // has to be cleared and removed from the Diagnosis Code(s) list too.
      hideIcdAndComorbidityForPanel(inputs, "", panelID);
      return;
    }
    inputs[icdPanelIndex]?.fields?.forEach((IcdPanelsFields: any) => {
      if (IcdPanelsFields.systemFieldName == "ICDPanels") {
        IcdPanelsFields?.panels?.forEach((panelsInfo: any) => {
          if (String(panelsInfo.panelID) === String(panelID)) {
            panelsInfo.isVisible = isVisible;
          }
        });
      }
    });
  };

  const normalizeTestName = (value: any) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const findDuplicateTestAcrossPanels = (
    inputsCopy: any,
    currentPanelIndex: number,
    testId: any,
    testName: any,
    testCode?: any
  ) => {
    if (!props?.preventDuplicationOfTests) return null;
    const currentPanels =
      props?.panels ||
      inputsCopy?.[props?.index]?.fields?.[props?.fieldIndex]?.panels ||
      [];
    const normalizedTargetName = normalizeTestName(testName);
    const normalizedTargetCode = normalizeTestName(testCode);

    for (let panelIndex = 0; panelIndex < currentPanels.length; panelIndex++) {
      if (panelIndex === currentPanelIndex) continue;

      const panel = currentPanels[panelIndex];
      const duplicateTest = panel?.testOptions?.find((testOption: any) => {
        if (!testOption?.isSelected) return false;
        const sameId =
          String(testOption?.testID || "") === String(testId || "");
        const sameName =
          normalizeTestName(testOption?.testName) === normalizedTargetName;
        const sameCode =
          normalizedTargetCode &&
          normalizeTestName(testOption?.testCode) === normalizedTargetCode;
        return sameId || (normalizedTargetName && sameName) || !!sameCode;
      });

      if (duplicateTest) {
        return {
          panelName: panel?.panelName || "",
          testName: duplicateTest?.testName || testName,
        };
      }
    }

    return null;
  };
  const toggleAOESection = (panelId: string, testId: string) => {
    const key = `${panelId}_${testId}`;
    setOpenAOESections((prevState: any) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };
  const parseTestAOES = (testAOES: string) => {
    try {
      if (!testAOES || testAOES.trim() === "") return null;
      return JSON.parse(testAOES);
    } catch (error) {
      console.error("Error parsing testAOES:", error);
      return null;
    }
  };

  const removeAOETestErrors = (panelId: string, testId: string) => {
    // Ensure panelId and testId are strings to match error structure
    const panelIdStr = String(panelId);
    const testIdStr = String(testId);

    props.setAOEErrors((prevErrors: any) => {
      if (!prevErrors?.[panelIdStr]?.[testIdStr]) {
        return prevErrors; // nothing to remove
      }

      const updatedErrors = { ...prevErrors };

      //  Remove only the testId under this panel
      delete updatedErrors[panelIdStr][testIdStr];

      //  Cleanup empty panel
      if (Object.keys(updatedErrors[panelIdStr]).length === 0) {
        delete updatedErrors[panelIdStr];
      }

      return updatedErrors;
    });
  };

  const clearAOEFieldsAndErrors = (panelId: string) => {
    let inputsCopy = JSON.parse(JSON.stringify(props?.Inputs));

    const panelToUpdate =
      inputsCopy[props.index]?.fields[props?.fieldIndex]?.panels?.find(
        (p: any) => p.panelID === panelId
      );

    if (!panelToUpdate || !panelToUpdate.testOptions) return;

    let hasAOEChanges = false;

    panelToUpdate.testOptions.forEach((testOption: any) => {
      if (testOption.testAOES && testOption.testAOES.trim() !== "") {
        try {
          const aoeData = JSON.parse(testOption.testAOES);
          let aoeDataChanged = false;

          aoeData.forEach((question: any) => {
            if (question.Answer) {
              question.Answer = "";
              question.IsSelectedAOEs = false;
              aoeDataChanged = true;
            }
          });

          if (aoeDataChanged) {
            testOption.testAOES = JSON.stringify(aoeData);
            testOption.isAOEsSelected = false;
            hasAOEChanges = true;
          }
        } catch (error) {
          console.error("Error clearing AOE fields:", error);
        }
      }
    });

    /*  REMOVE ONLY THIS PANEL FROM AOE ERRORS */
    // Ensure panelId is string to match error structure
    const panelIdStr = String(panelId);
    props.setAOEErrors((prevErrors: any) => {
      if (!prevErrors?.[panelIdStr]) return prevErrors;

      const updatedErrors = { ...prevErrors };
      delete updatedErrors[panelIdStr];
      return updatedErrors;
    });

    /*  UPDATE INPUTS ONLY IF AOE DATA CHANGED */
    if (hasAOEChanges) {
      let newInputs = assignFormValues(
        inputsCopy,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        panelsArrToSend,
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );

      newInputs?.then((res) => {
        let infectiousDataCopy = JSON.parse(
          JSON.stringify(props?.infectiousData)
        );
        infectiousDataCopy[
          FindIndex(props?.infectiousData, props?.ArrayReqId)
        ].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    }
  };

  const handleAOEAnswerChange = (
    panelId: string,
    testId: string,
    questionId: number,
    answerId: number,
    value: string
  ) => {
    let inputsCopy = JSON.parse(JSON.stringify(props?.Inputs));
    const panel = inputsCopy[props.index]?.fields[props?.fieldIndex]?.panels?.find(
      (p: any) => p.panelID === panelId
    );
    if (!panel) return;

    const testOption = panel.testOptions?.find((t: any) => t.testID === testId);
    if (!testOption || !testOption.testAOES) return;

    try {
      const aoeData = JSON.parse(testOption.testAOES);
      const questionIndex = aoeData.findIndex((q: any) => q.QuestionId === questionId);
      if (questionIndex === -1) return;

      // AOEsAnswers is read-only, only used to display options
      // Only update the question's Answer field
      aoeData[questionIndex].Answer = value;
      aoeData[questionIndex].IsSelectedAOEs = value !== null && value !== "" && String(value).trim() !== "";

      // Update testAOES string
      testOption.testAOES = JSON.stringify(aoeData);
      testOption.isAOEsSelected = aoeData.some((q: any) => q.IsSelectedAOEs);

      // Validate AOE questions after answer change - use updated aoeData directly
      // Create a temporary testOption with updated data for validation
      // validateAOEQuestions(updatedTestOption, panelId.toString(), testId);
      clearAOEFieldsAndErrors(panelId.toString());
      // Update the inputs
      let newInputs = assignFormValues(
        inputsCopy,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        panelsArrToSend,
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );

      newInputs?.then((res) => {
        let infectiousDataCopy = JSON.parse(JSON.stringify(props?.infectiousData));
        infectiousDataCopy[FindIndex(props?.infectiousData, props?.ArrayReqId)].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    } catch (error) {
      console.error("Error updating AOE answer:", error);
    }
  };
  const handleAOEMultipleSelectionChange = (
    panelId: string,
    testId: string,
    questionId: number,
    answerId: number,
    value: string,
    isChecked: boolean
  ) => {
    let inputsCopy = JSON.parse(JSON.stringify(props?.Inputs));
    const panel = inputsCopy[props.index]?.fields[props?.fieldIndex]?.panels?.find(
      (p: any) => p.panelID === panelId
    );
    if (!panel) return;

    const testOption = panel.testOptions?.find((t: any) => t.testID === testId);
    if (!testOption || !testOption.testAOES) return;

    try {
      const aoeData = JSON.parse(testOption.testAOES);
      const questionIndex = aoeData.findIndex((q: any) => q.QuestionId === questionId);
      if (questionIndex === -1) return;

      // Parse existing answers (comma-separated string or array)
      // AOEsAnswers is read-only, only used to display options
      let selectedAnswers: string[] = [];
      if (aoeData[questionIndex].Answer) {
        if (typeof aoeData[questionIndex].Answer === 'string') {
          selectedAnswers = aoeData[questionIndex].Answer.split(',').map((a: string) => a.trim()).filter((a: string) => a !== '');
        } else if (Array.isArray(aoeData[questionIndex].Answer)) {
          selectedAnswers = [...aoeData[questionIndex].Answer];
        }
      }

      // Add or remove the selected value (store exactly as it comes from AOEsAnswers)
      if (isChecked) {
        if (!selectedAnswers.includes(value)) {
          selectedAnswers.push(value);
        }
      } else {
        selectedAnswers = selectedAnswers.filter((a: string) => a !== value);
      }

      // Update the answer as comma-separated string (store values exactly as they come)
      aoeData[questionIndex].Answer = selectedAnswers.join(',');
      aoeData[questionIndex].IsSelectedAOEs = selectedAnswers.length > 0;
      if (Array.isArray(aoeData[questionIndex].AOEsAnswers)) {
        aoeData[questionIndex].AOEsAnswers = aoeData[questionIndex].AOEsAnswers.map((answer: any) => {
          const answerValue = answer?.Answer ?? answer?.AnswerCode ?? answer?.AnswerId?.toString() ?? "";
          const isSelectedAnswer =
            selectedAnswers.includes(answer?.Answer) ||
            selectedAnswers.includes(answerValue) ||
            selectedAnswers.includes(answer?.AnswerCode) ||
            selectedAnswers.includes(answer?.AnswerId?.toString());
          return {
            ...answer,
            IsSelected: !!isSelectedAnswer,
          };
        });
      }

      // Update testAOES string
      testOption.testAOES = JSON.stringify(aoeData);
      testOption.isAOEsSelected = aoeData.some((q: any) => q.IsSelectedAOEs);

      // Validate AOE questions after answer change - use updated aoeData directly      const updatedTestOption = { ...testOption, testAOES: JSON.stringify(aoeData) };
      // validateAOEQuestions(updatedTestOption, panelId.toString(), testId);

      // Update the inputs
      let newInputs = assignFormValues(
        inputsCopy,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        panelsArrToSend,
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );

      newInputs?.then((res) => {
        let infectiousDataCopy = JSON.parse(JSON.stringify(props?.infectiousData));
        infectiousDataCopy[FindIndex(props?.infectiousData, props?.ArrayReqId)].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    } catch (error) {
      console.error("Error updating AOE multiple selection answer:", error);
    }
  };
  /**
   * The field's stored value must be the panels the user actually has selected.
   * Writing the append-only `storeChild` state or a panel's full `testOptions`
   * list left `defaultValue` non-empty while nothing was selected, which is what
   * let the required Testing Option pass its `min(1)` check on a retry.
   * `SelectablePanel` always wrote the filtered list, which is why only
   * `MultipleSelectablePanel` showed the bug.
   */
  const getSelectedPanels = (inputs: any) =>
    (inputs?.[props?.index]?.fields?.[props?.fieldIndex]?.panels ?? []).filter(
      (panel: any) => panel?.isSelected
    );

  const handleChangeParent = (
    panelID: number,
    panelName: string,
    checked: boolean,
    index: number
  ) => {
    const livePanels =
      props?.Inputs?.[props?.index]?.fields?.[props?.fieldIndex]?.panels ??
      props?.panels ??
      [];
    const activeDisabledPanelIds = computeDisabledPanelIds(
      livePanels,
      props?.panelCombinations
    );

    // Block selection if this panel is mutually exclusive with an already-selected panel.
    // The user sees a friendly message instead of a silent no-op.
    if (checked && activeDisabledPanelIds.has(String(panelID))) {
      const conflictingName = getConflictingPanelNameForCombination(
        panelID,
        livePanels,
        props?.panelCombinations
      );
      setMutualExclusivityError(
        conflictingName
          ? `"${panelName}" cannot be selected because "${conflictingName}" is already selected. Only one panel can be selected from this group.`
          : "Only one panel can be selected from this group. Please deselect the current selection first."
      );
      return;
    }
    setMutualExclusivityError("");
    props.fields.enableRule = "";
    const name = panelName;
    let panelsArrToAppendCopy = [...panelsArrToSend];
    let inputsCopy = JSON?.parse(JSON?.stringify(props?.Inputs));
    updateDuplicateTestErrorForPanel(panelID, "");

    if (checked) {
      const currentPanel =
        inputsCopy[props.index].fields[props?.fieldIndex].panels[index];

      currentPanel?.testOptions?.forEach((testOptions: any) => {
        const duplicateSelection = findDuplicateTestAcrossPanels(
          inputsCopy,
          index,
          testOptions?.testID,
          testOptions?.testName,
          testOptions?.testCode
        );

        // Select only non-duplicate tests; keep duplicates unchecked.
        if (duplicateSelection) {
          testOptions.isSelected = false;
          return;
        }

        testOptions.isSelected = true;
        // Auto-expand AOE section if testAOES exists
        if (testOptions.testAOES && testOptions.testAOES.trim() !== "") {
          const aoeKey = `${panelID}_${testOptions.testID}`;
          setOpenAOESections((prev: any) => ({
            ...prev,
            [aoeKey]: true,
          }));
          // Validate AOE questions when test is selected
          //  validateAOEQuestions(testOptions, panelID.toString(), testOptions.testID);
        }
      });

      currentPanel.isSelected = currentPanel?.testOptions?.some(
        (test: any) => test?.isSelected
      );

      // Parent-check should silently skip duplicates without showing panel error
      updateDuplicateTestErrorForPanel(panelID, "");

      let ICDPanelIndex = getICDPanelsIndex(inputsCopy);
      setIcdPanelVisibility(
        inputsCopy,
        ICDPanelIndex,
        panelID,
        currentPanel?.isSelected
      );

      const panelsWithoutCurrent = panelsArrItemRemoval(
        panelID,
        panelsArrToAppendCopy
      );
      let finalisedPanelsArrToSend = currentPanel?.isSelected
        ? [...panelsWithoutCurrent, currentPanel]
        : panelsWithoutCurrent;

      setPanelsArrToSend(finalisedPanelsArrToSend);
      let newInputs = assignFormValues(
        inputsCopy,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        finalisedPanelsArrToSend,
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );
      newInputs?.then((res) => {
        let infectiousDataCopy = JSON?.parse(
          JSON?.stringify(props?.infectiousData)
        );
        infectiousDataCopy[
          FindIndex(props?.infectiousData, props?.ArrayReqId)
        ].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    }
    if (!checked) {
      let ICDPanelIndex = getICDPanelsIndex(props?.Inputs);

      inputsCopy[props.index].fields[props?.fieldIndex].panels[
        index
      ].isSelected = false;
      // Clear AOE fields and errors for unselected panel
      clearAOEFieldsAndErrors(panelID.toString());

      inputsCopy[props.index].fields[props?.fieldIndex].panels[
        index
      ].testOptions.forEach((testOptions: any) => {
        testOptions.isSelected = false;
      });
      let finalisedPanelsArrToSend = panelsArrItemRemoval(
        panelID,
        panelsArrToAppendCopy
      );
      setIcdPanelVisibility(inputsCopy, ICDPanelIndex, panelID, false);
      setPanelsArrToSend(finalisedPanelsArrToSend);
      let newInputs = assignFormValues(
        inputsCopy,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        finalisedPanelsArrToSend,
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );
      newInputs?.then((res) => {
        let infectiousDataCopy = JSON?.parse(
          JSON?.stringify(props?.infectiousData)
        );
        infectiousDataCopy[
          FindIndex(props?.infectiousData, props?.ArrayReqId)
        ].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    }
  };
  const handleChangeChild = (
    testID: string,
    checked: boolean,
    parentPanelName: string,
    index: number,
    childIndex: number,
    panelID: any
  ) => {
    props.fields.enableRule = "";
    let inputsCopy = JSON.parse(JSON?.stringify(props?.Inputs));
    // let panelsArrToAppendCopy = JSON.parse(JSON?.stringify(panelsArrToSend));
    // let panelsCopy = [...props?.panels];
    let ICDPanelIndex = getICDPanelsIndex(inputsCopy);
    if (checked) {
      const selectedTest =
        inputsCopy[props.index].fields[props?.fieldIndex].panels[index]
          ?.testOptions?.[childIndex];
      const duplicateSelection = findDuplicateTestAcrossPanels(
        inputsCopy,
        index,
        selectedTest?.testID,
        selectedTest?.testName,
        selectedTest?.testCode
      );

      if (duplicateSelection) {
        updateDuplicateTestErrorForPanel(
          panelID,
          `${selectedTest?.testName} is already selected in ${duplicateSelection.panelName}.`
        );
        return;
      }
      updateDuplicateTestErrorForPanel(panelID, "");

      // Update child checkbox state
      const testOption = inputsCopy[props.index].fields[props?.fieldIndex].panels[
        index
      ].testOptions[childIndex];
      testOption.isSelected = true;
      // Validate AOE questions when test is selected
      if (testOption.testAOES && testOption.testAOES.trim() !== "") {
        const panel = inputsCopy[props.index].fields[props?.fieldIndex].panels[index];
        //validateAOEQuestions(testOption, panel.panelID.toString(), testOption.testID);
      }
      // Update parent checkbox state
      inputsCopy[props.index].fields[props?.fieldIndex].panels[
        index
      ].isSelected = true;
      const currentPanel =
        inputsCopy[props.index].fields[props?.fieldIndex].panels[index];
      const allChildUnchecked = inputsCopy[props.index].fields[
        props?.fieldIndex
      ].panels[index].testOptions.every((option: any) => !option.isSelected);
      // If all child checkboxes are unchecked, update parent checkbox state and panel visibility
      if (allChildUnchecked) {
        // Uncheck the parent checkbox
        inputsCopy[props.index].fields[props?.fieldIndex].panels[
          index
        ].isSelected = false;
        // Update parent panel visibility
      }
      // Show/hide this panel's ICD-10 group to match the panel's resulting checked state
      setIcdPanelVisibility(
        inputsCopy,
        ICDPanelIndex,
        panelID,
        currentPanel?.isSelected
      );

      let newInputs = assignFormValues(
        inputsCopy,
        // props?.dependenceyControls,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        getSelectedPanels(inputsCopy),
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );
      newInputs?.then((res) => {
        let infectiousDataCopy = JSON?.parse(
          JSON?.stringify(props?.infectiousData)
        );
        infectiousDataCopy[
          FindIndex(props?.infectiousData, props?.ArrayReqId)
        ].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    } else {
      updateDuplicateTestErrorForPanel(panelID, "");

      // Uncheck the child checkbox
      const testOptionUncheck = inputsCopy[props.index].fields[props?.fieldIndex].panels[
        index
      ].testOptions[childIndex];
      testOptionUncheck.isSelected = false;
      // Clear AOE errors when test is unchecked
      if (testOptionUncheck.testAOES && testOptionUncheck.testAOES.trim() !== "") {
        try {
          const panel = inputsCopy[props.index].fields[props?.fieldIndex].panels[index];
          const aoeData = JSON.parse(testOptionUncheck.testAOES);

          aoeData.forEach((question: any) => {
            const questionCode = question.QuestionCode || `AOE_${panel.panelID}_${testOptionUncheck.testID}_${question.QuestionId}`;
          });
          removeAOETestErrors(panelID, testID);

        } catch (error) {
          console.error("Error clearing AOE errors:", error);
        }
      }
      const currentPanel =
        inputsCopy[props.index].fields[props?.fieldIndex].panels[index];
      // Check if all child checkboxes in the parent panel are unchecked
      const allChildUnchecked = inputsCopy[props.index].fields[
        props?.fieldIndex
      ].panels[index].testOptions.every((option: any) => !option.isSelected);
      //  If all child checkboxes are unchecked, update parent checkbox state and panel visibility
      if (allChildUnchecked) {
        // Uncheck the parent checkbox
        inputsCopy[props.index].fields[props?.fieldIndex].panels[
          index
        ].isSelected = false;
      }
      // Show/hide this panel's ICD-10 group to match the panel's resulting checked state
      setIcdPanelVisibility(
        inputsCopy,
        ICDPanelIndex,
        panelID,
        currentPanel?.isSelected
      );
      let newInputs = assignFormValues(
        inputsCopy,
        // props?.dependenceyControls,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        getSelectedPanels(inputsCopy),
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );
      newInputs?.then((res) => {
        let infectiousDataCopy = JSON?.parse(
          JSON?.stringify(props?.infectiousData)
        );
        infectiousDataCopy[
          FindIndex(props?.infectiousData, props?.ArrayReqId)
        ].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    }
    // Update inputs and req states
  };

  const handleMultiSelect = (
    parentPanelName: string,
    index: number,
    selectedTests: any
  ) => {
    props.fields.enableRule = "";
    let inputsCopy = JSON.parse(JSON?.stringify(props?.Inputs));
    const panelId =
      inputsCopy[props.index].fields[props?.fieldIndex].panels[index]?.panelID;
    updateDuplicateTestErrorForPanel(panelId, "");

    let ICDPanelIndex = getICDPanelsIndex(inputsCopy);

    const panels =
      inputsCopy[props.index].fields[props?.fieldIndex].panels[index];
    const filteredSelectedTests = selectedTests.filter((selected: any) => {
      const selectedTest = panels?.testOptions?.find(
        (test: any) => String(test?.testID) === String(selected?.value)
      );
      const duplicateSelection = findDuplicateTestAcrossPanels(
        inputsCopy,
        index,
        selected?.value,
        selectedTest?.testName || selected?.label,
        selectedTest?.testCode || selected?.code
      );

      if (duplicateSelection) {
        updateDuplicateTestErrorForPanel(
          panelId,
          `${selectedTest?.testName || selected?.label} is already selected in ${duplicateSelection.panelName}.`
        );
        return false;
      }
      return true;
    });

    // Loop through testOptions and update their isSelected state
    panels.testOptions.forEach((test: any, testIndex: number) => {
      const isSelected = filteredSelectedTests.some(
        (selected: any) => selected.value === test.testID
      );
      test.isSelected = isSelected;
      // Validate AOE questions when test is selected
      if (isSelected && test.testAOES && test.testAOES.trim() !== "") {
        // validateAOEQuestions(test, panels.panelID.toString(), test.testID);
      }
      else if (!isSelected && test.testAOES && test.testAOES.trim() !== "") {


        removeAOETestErrors(panels.panelID, test.testID)
        // Clear AOE errors when test is unchecked
      }
    });

    // Update parent checkbox state: if all child checkboxes are unchecked, uncheck parent
    panels.isSelected = panels.testOptions.some((test: any) => test.isSelected);

    // Show/hide this panel's ICD-10 group to match the panel's resulting checked state
    setIcdPanelVisibility(inputsCopy, ICDPanelIndex, panelId, panels.isSelected);

    let newInputs = assignFormValues(
      inputsCopy,
      props?.index,
      props?.depControlIndex,
      props?.fieldIndex,
      getSelectedPanels(inputsCopy),
      props?.isDependency,
      props?.repeatFieldSection,
      props?.isDependencyRepeatFields,
      props?.repeatFieldIndex,
      props?.repeatDependencySectionIndex,
      props?.repeatDepFieldIndex,
      undefined,
      props?.setInputs
    );

    newInputs?.then((res) => {
      let infectiousDataCopy = JSON?.parse(
        JSON?.stringify(props?.infectiousData)
      );
      infectiousDataCopy[
        FindIndex(props?.infectiousData, props?.ArrayReqId)
      ].sections = res;
      props?.setInfectiousData([...infectiousDataCopy]);
    });
  };

  const checkDuplicateForSearchSelection = (
    panelIndex: number,
    option: any
  ) => {
    if (!props?.preventDuplicationOfTests) return null;
    const inputsCopy = JSON.parse(JSON?.stringify(props?.Inputs));
    const panel =
      inputsCopy?.[props.index]?.fields?.[props?.fieldIndex]?.panels?.[
      panelIndex
      ];
    const selectedTest = panel?.testOptions?.find(
      (test: any) => String(test?.testID) === String(option?.value)
    );

    return findDuplicateTestAcrossPanels(
      inputsCopy,
      panelIndex,
      option?.value,
      selectedTest?.testName || option?.label,
      selectedTest?.testCode || option?.code
    );
  };

  const updateDuplicateTestErrorForPanel = (
    panelId: string | number,
    message: string
  ) => {
    const panelKey = String(panelId);
    setDuplicateTestErrorsByPanelId((prev) => {
      if (!message) {
        const updated = { ...prev };
        delete updated[panelKey];
        return updated;
      }
      return {
        ...prev,
        [panelKey]: message,
      };
    });
  };

  const renderPanelDuplicateError = (panelId: string | number) => {
    const panelError = duplicateTestErrorsByPanelId[String(panelId)];
    if (!panelError) return null;
    return (
      <div className="form__error mt-2">
        <span>{panelError}</span>
      </div>
    );
  };

  const setpanelsArrToSendForEdit = () => {
    let panelsCopy = [...props?.panels];
    let filteredPanelsCopy = panelsCopy?.filter(
      (panelsData: any) => panelsData?.isSelected
    );
    let panelNameArr: any = [];
    filteredPanelsCopy?.forEach((panelData: any) => {
      panelNameArr?.push(panelData?.panelName);
    });
    let inputsCopy = [...props?.Inputs];
    // On edit the saved codes are restored into the Diagnosis Code(s) list, but
    // the ICD-10 cards of the already selected panels stay hidden. Reveal them
    // again so every code configured for those panels is available, not just
    // the one picked when the requisition was created.
    let ICDPanelIndex = getICDPanelsIndex(inputsCopy);
    filteredPanelsCopy?.forEach((panelData: any) => {
      setIcdPanelVisibility(
        inputsCopy,
        ICDPanelIndex,
        panelData?.panelID,
        true
      );
    });
    assignFormValues(
      inputsCopy,
      // props?.dependenceyControls,
      props?.index,
      props?.depControlIndex,
      props?.fieldIndex,
      filteredPanelsCopy,
      props?.isDependency,
      props?.repeatFieldSection,
      props?.isDependencyRepeatFields,
      props?.repeatFieldIndex,
      props?.repeatDependencySectionIndex,
      props?.repeatDepFieldIndex,
      undefined,
      props?.setInputs
    );
    setPanelsArrToSend(filteredPanelsCopy);
  };
  const divElement = useRef<HTMLDivElement | null>(null); // Initialize ref for div
  useEffect(() => {
    // Scroll to the div if props.error is present
    if (props.error && divElement.current) {
      divElement.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    props.setErrorFocussedInput && props.setErrorFocussedInput();
  }, [props?.errorFocussedInput]);
  return (
    <>
      {props.error && (
        <div className="form__error">
          <span>{t(props.error)}</span>
        </div>
      )}
      {!props?.preventDuplicationOfTests && props?.ArrayReqId !== 53 && (
        <div className="text-muted mb-2">
          <span>You can select the same test in multiple panels.</span>
        </div>
      )}
      <div id={props?.name} ref={divElement} tabIndex={-1}></div>
      {/* Shown when a user tries to select a panel that is blocked by mutual exclusivity rules */}
      {mutualExclusivityError && (
        <div className="alert alert-warning d-flex align-items-center py-2 px-4 mb-3" role="alert">
          <span>{mutualExclusivityError}</span>
        </div>
      )}
      {props?.panels?.map((options: any, index: number) => {
        const isPanelDisabled = disabledPanelIds.has(String(options?.panelID));
        const panelType = options?.panelType;
        const testOptions = options?.testOptions;

        const selectedTestOptions = testOptions.filter(
          (options: any) => options.isSelected
        );

        const formattedOptions = testOptions.map((test: any) => ({
          value: test.testID,
          label: test.testName,
        }));

        if (panelType === PanelsTypes.Search) {
          return (
            <SearchInput
              displayType={props?.displayType}
              panelName={options?.panelName}
              testOptions={options?.testOptions}
              disabled={props?.disabled}
              handleMultiSelect={handleMultiSelect}
              selectedTestOptions={selectedTestOptions}
              index={index}
              panelId={options?.panelID}
              openAOESections={openAOESections}
              toggleAOESection={toggleAOESection}
              parseTestAOES={parseTestAOES}
              handleAOEAnswerChange={handleAOEAnswerChange}
              handleAOEMultipleSelectionChange={handleAOEMultipleSelectionChange}
              AOEErrors={props.AOEErrors}
              duplicateTestError={
                duplicateTestErrorsByPanelId[String(options?.panelID)] || ""
              }
              checkDuplicateForSearchSelection={checkDuplicateForSearchSelection}
              setDuplicateTestError={(message: string) =>
                updateDuplicateTestErrorForPanel(options?.panelID, message)
              }
            />
          );
        }

        if (panelType === PanelsTypes.ListOnly) {
          return (
            <div className={`${props?.displayType}`}>
              <div className="row">
                <div className="col-lg-12">
                  <div className="card border border-gray-300 mb-3 rounded">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center rounded min-h-35px px-4">
                      <div className="col-12">
                        <label className="form-check form-check-sm form-check-solid col-12">
                          <input
                            className="form-check-input mr-2 h-20px w-20px"
                            type="checkbox"
                            checked={options?.isSelected}
                            onChange={(e: any) => {
                              handleChangeParent(
                                options?.panelID,
                                options?.panelName,
                                e.target.checked,
                                index
                              );
                            }}
                          />
                          {<span className="fw-600">{options?.panelName}</span>}
                        </label>
                      </div>
                    </div>
                    <div className="card-body py-md-4 py-3 col-12">
                      {renderPanelDuplicateError(options?.panelID)}
                      <div className="row">
                        {options?.testOptions?.map(
                          (optionsChild: any, childIndex: any) => {
                            return (
                              <div
                                className={
                                  options?.testOptions.length > 1
                                    ? "mb-3 col-xxl-3 col-xl-4 col-lg-4 col-md-6 col-sm-12"
                                    : "mb-3 col-xl-12 col-lg-12 col-md-12 col-sm-12"
                                }
                              >
                                <label className="form-check form-check-sm align-items-start form-check-solid col-12 d-flex justify-content-between align-items-center">
                                  <div className="d-flex align-items-center flex-grow-1">
                                    <span className="fw-400 text-gray-800">
                                      {optionsChild?.testName}
                                      {optionsChild?.testCode && (
                                        <>
                                          {' - '}
                                          <span className="text-muted">{optionsChild?.testCode}</span>
                                        </>
                                      )}
                                    </span>
                                  </div>
                                  {optionsChild?.specimenType && (
                                    <span className="text-muted ms-3 flex-shrink-0">
                                      {optionsChild?.specimenType}
                                    </span>
                                  )}
                                </label>
                                <GroupTestList
                                  testOption={optionsChild}
                                  indent={false}
                                />
                                <AOEQuestions
                                  testOption={optionsChild}
                                  panelId={options?.panelID}
                                  panelName={options?.panelName}
                                  openAOESections={openAOESections}
                                  toggleAOESection={toggleAOESection}
                                  parseTestAOES={parseTestAOES}
                                  handleAOEAnswerChange={handleAOEAnswerChange}
                                  handleAOEMultipleSelectionChange={handleAOEMultipleSelectionChange}
                                  AOEErrors={props.AOEErrors}
                                />
                              </div>
                            );
                          }
                        )}
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        return options.isVisible && panelType === PanelsTypes.SelectableList ? (
          <div className={`${props?.displayType}`}>
            <div className="row">
              <div className="col-lg-12">
                <div className={`card border ${isPanelDisabled ? "border-gray-200" : "border-gray-300"} mb-3 rounded`}
                  style={isPanelDisabled ? { opacity: 0.55 } : {}}>
                  <div
                    className="card-header bg-light d-flex justify-content-between align-items-center rounded min-h-35px px-4"
                    style={isPanelDisabled ? { cursor: "not-allowed" } : {}}
                    onClick={() => {
                      if (isPanelDisabled) {
                        const conflictingName = getConflictingPanelName(options?.panelID);
                        setMutualExclusivityError(
                          conflictingName
                            ? `"${options?.panelName}" cannot be selected because "${conflictingName}" is already selected. Only one panel can be selected from this group.`
                            : "Only one panel can be selected from this group. Please deselect the current selection first."
                        );
                      }
                    }}
                  >
                    <div className="col-12">
                      <label className="form-check form-check-sm form-check-solid col-12">
                        <input
                          className="form-check-input mr-2 h-20px w-20px"
                          type="checkbox"
                          checked={options?.isSelected}
                          disabled={isPanelDisabled}
                          onChange={(e: any) => {
                            handleChangeParent(
                              options?.panelID,
                              options?.panelName,
                              e.target.checked,
                              index
                            );
                          }}
                        />

                        {<span className="fw-600">{options?.panelName}</span>}
                      </label>
                    </div>
                  </div>
                  <div className="card-body py-md-4 py-3 col-12">
                    {renderPanelDuplicateError(options?.panelID)}
                    <div className="row">
                      {options?.testOptions?.map(
                        (optionsChild: any, childIndex: any) => {
                          return (
                            <div
                              key={childIndex}
                              className={
                                options?.testOptions.length > 1
                                  ? "mb-3 col-xxl-3 col-xl-4 col-lg-4 col-md-6 col-sm-12"
                                  : "mb-3 col-xl-12 col-lg-12 col-md-12 col-sm-12"
                              }
                            >
                              <label className="form-check form-check-sm align-items-start form-check-solid col-12 d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center flex-grow-1">
                                  <input
                                    className="form-check-input me-2 h-20px w-20px flex-shrink-0"
                                    type="checkbox"
                                    checked={optionsChild?.isSelected}
                                    disabled={isPanelDisabled}
                                    onChange={(e: any) => {
                                      handleChangeChild(
                                        optionsChild?.testID,
                                        e?.target?.checked,
                                        options?.panelName,
                                        index,
                                        childIndex,
                                        options?.panelID
                                      );
                                      // Auto-expand AOE section if testAOES exists and checkbox is checked
                                      if (e.target.checked && optionsChild?.testAOES && optionsChild?.testAOES.trim() !== "") {
                                        const aoeKey = `${options?.panelID}_${optionsChild?.testID}`;
                                        setOpenAOESections((prev: any) => ({
                                          ...prev,
                                          [aoeKey]: true,
                                        }));
                                      }
                                    }}
                                  />
                                  <span className="fw-400 text-gray-800">
                                    {optionsChild?.testName}
                                    {optionsChild?.testCode && (
                                      <>
                                        {' - '}
                                        <span className="text-muted">{optionsChild?.testCode}</span>
                                      </>
                                    )}
                                  </span>
                                </div>
                                {optionsChild?.specimenType && (
                                  <span className="text-muted ms-3 flex-shrink-0">
                                    {optionsChild?.specimenType}
                                  </span>
                                )}
                              </label >
                              <GroupTestList testOption={optionsChild} />
                              <AOEQuestions
                                testOption={optionsChild}
                                panelId={options?.panelID}
                                panelName={options?.panelName}
                                openAOESections={openAOESections}
                                toggleAOESection={toggleAOESection}
                                parseTestAOES={parseTestAOES}
                                handleAOEAnswerChange={handleAOEAnswerChange}
                                handleAOEMultipleSelectionChange={handleAOEMultipleSelectionChange}
                                AOEErrors={props.AOEErrors}
                              />
                            </div >
                          );
                        }
                      )}
                    </div >
                  </div >
                </div >
              </div >
            </div >
          </div >
        ) : (
          options.isVisible &&
          panelType === PanelsTypes.SelectableListForTest && (
            <div className={`${props?.displayType}`}>
              <div className="row">
                <div className="col-lg-12">
                  <div className="card border border-gray-300 mb-3 rounded">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center rounded min-h-35px px-4">
                      <div className="col-12">
                        <label className="form-check form-check-sm form-check-solid col-12">
                          {
                            <span className="fw-600">
                              {options?.panelName}
                            </span>
                          }
                        </label>
                      </div>
                    </div>
                    <div className="card-body py-md-4 py-3 col-12">
                      {renderPanelDuplicateError(options?.panelID)}
                      <div className="row">
                        {options?.testOptions?.map(
                          (optionsChild: any, childIndex: any) => {
                            return (
                              <div
                                key={childIndex}
                                className={
                                  options?.testOptions.length > 1
                                    ? "mb-3 col-xxl-3 col-xl-4 col-lg-4 col-md-6 col-sm-12"
                                    : "mb-3 col-xl-12 col-lg-12 col-md-12 col-sm-12"
                                }
                              >
                                <label className="form-check form-check-sm align-items-start form-check-solid col-12 d-flex justify-content-between align-items-center">
                                  <div className="d-flex align-items-center flex-grow-1">
                                    <input
                                      className="form-check-input me-2 h-20px w-20px flex-shrink-0"
                                      type="checkbox"
                                      onChange={(e: any) => {
                                        handleChangeChild(
                                          optionsChild?.testID,
                                          e?.target?.checked,
                                          options?.panelName,
                                          index,
                                          childIndex,
                                          options?.panelID
                                        );
                                        // Auto-expand AOE section if testAOES exists and checkbox is checked
                                        if (e.target.checked && optionsChild?.testAOES && optionsChild?.testAOES.trim() !== "") {
                                          const aoeKey = `${options?.panelID}_${optionsChild?.testID}`;
                                          setOpenAOESections((prev: any) => ({
                                            ...prev,
                                            [aoeKey]: true,
                                          }));
                                        }
                                      }}
                                      checked={optionsChild?.isSelected}
                                    />
                                    <span className="fw-400 text-gray-800">
                                      {optionsChild?.testName}
                                      {optionsChild?.testCode && (
                                        <>
                                          {' - '}
                                          <span className="text-muted">{optionsChild?.testCode}</span>
                                        </>
                                      )}
                                    </span>
                                  </div>
                                  {optionsChild?.specimenType && (
                                    <span className="text-muted ms-3 flex-shrink-0">
                                      {optionsChild?.specimenType}
                                    </span>
                                  )}
                                </label>
                                <GroupTestList testOption={optionsChild} />
                                <AOEQuestions
                                  testOption={optionsChild}
                                  panelId={options?.panelID}
                                  panelName={options?.panelName}
                                  openAOESections={openAOESections}
                                  toggleAOESection={toggleAOESection}
                                  parseTestAOES={parseTestAOES}
                                  handleAOEAnswerChange={handleAOEAnswerChange}
                                  handleAOEMultipleSelectionChange={handleAOEMultipleSelectionChange}
                                  AOEErrors={props.AOEErrors}
                                />
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        );
      })}
    </>
  );
};
const AOEQuestions = (props: any) => {
  const {
    testOption,
    panelId,
    panelName,
    openAOESections,
    toggleAOESection,
    parseTestAOES,
    handleAOEAnswerChange,
    handleAOEMultipleSelectionChange,
    AOEErrors
  } = props;

  const hasAOES =
    testOption?.testAOES && testOption?.testAOES.trim() !== "";

  const aoeData = hasAOES ? parseTestAOES(testOption.testAOES) : null;

  const aoeKey = `${panelId}_${testOption?.testID}`; const isAOEOpen = openAOESections[aoeKey] || false;

  /* PANEL HAS ERRORS (ANY TEST UNDER IT) */


  if (!hasAOES || !testOption?.isSelected || !aoeData) return null;

  return (
    <div className="ms-4 mt-2 mb-3">
      {/* ===== AOE HEADER ===== */}
      <div className="d-flex align-items-center">
        <span className="fw-600 text-primary">
          Additional Questions
        </span>
      </div>

      {/* ===== AOE BODY ===== */}
      <div className="mt-3 ps-4 border-start border-2 border-primary">
        {aoeData.map((question: any, qIndex: number) => {
          const questionCode =
            question.QuestionCode ||
            `AOE_${panelId}_${testOption?.testID}_${question.QuestionId}`;

          // Ensure panelId and testID are strings to match error structure
          const panelIdStr = String(panelId);
          const testIdStr = String(testOption?.testID);

          const errorMessage =
            AOEErrors?.[panelIdStr]?.[testIdStr]?.[questionCode];

          return (
            <div key={qIndex} className="mb-4">
              {/* QUESTION LABEL */}
              <div
                className="form-label fw-bold mb-2 text-gray-700"
                style={{ userSelect: "none" }}
              >
                {question.Question}
                {question.IsRequiredAnswer && (
                  <span className="text-danger ms-1">*</span>
                )}
              </div>

              {/* ERROR MESSAGE */}
              {errorMessage && (
                <div className="text-danger small mb-2">
                  {errorMessage}
                </div>
              )}

              {/* TEXT */}
              {question.AnswerDataType === "Text" && (
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter answer"
                  value={question.Answer || ""}
                  onChange={(e) =>
                    handleAOEAnswerChange(
                      panelId,
                      testOption?.testID,
                      question.QuestionId,
                      question.AOEsAnswers?.[0]?.AnswerId || 0,
                      e.target.value
                    )
                  }
                />
              )}

              {/* NUMBER */}
              {question.AnswerDataType === "Number" && (
                <input
                  type="number"
                  className="form-control"
                  placeholder="Enter number"
                  value={question.Answer || ""}
                  onChange={(e) =>
                    handleAOEAnswerChange(
                      panelId,
                      testOption?.testID,
                      question.QuestionId,
                      question.AOEsAnswers?.[0]?.AnswerId || 0,
                      e.target.value
                    )
                  }
                />
              )}

              {/* DATE */}
              {question.AnswerDataType === "Date" && (
                <input
                  type="date"
                  className="form-control"
                  value={formatDateToYYYYMMDD(question.Answer || "")}
                  onChange={(e) => {
                    const formattedDate = formatDateToMMDDYYYY(e.target.value);
                    handleAOEAnswerChange(
                      panelId,
                      testOption?.testID,
                      question.QuestionId,
                      question.AOEsAnswers?.[0]?.AnswerId || 0,
                      formattedDate
                    );
                  }}
                />
              )}

              {/* MULTIPLE CHOICE */}
              {question.AnswerDataType === "Multiple Choice" && (
                <div>
                  {question.AOEsAnswers?.map(
                    (answer: any, aIndex: number) => {
                      const answerValue =
                        answer.Answer ??
                        answer.AnswerCode ??
                        answer.AnswerId?.toString();

                      const answerLabel =
                        answer.Answer ??
                        answer.AnswerCode ??
                        `Option ${aIndex + 1}`;

                      const isSelected = !!answer?.IsSelected;

                      return (
                        <div
                          key={aIndex}
                          className="form-check mb-2"
                        >
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={(e) =>
                              handleAOEMultipleSelectionChange(
                                panelId,
                                testOption?.testID,
                                question.QuestionId,
                                answer.AnswerId,
                                answerValue,
                                e.target.checked
                              )
                            }
                          />
                          <label className="form-check-label">
                            {answerLabel}
                          </label>
                        </div>
                      );
                    }
                  )}
                </div>
              )}

              {/* MULTIPLE SELECTION */}
              {question.AnswerDataType === "Single Selection" && (
                <div>
                  {question.AOEsAnswers?.map(
                    (answer: any, aIndex: number) => {
                      const answerValue =
                        answer.Answer ??
                        answer.AnswerCode ??
                        answer.AnswerId?.toString();

                      const answerLabel =
                        answer.Answer ??
                        answer.AnswerCode ??
                        `Option ${aIndex + 1}`;

                      const isSelected =
                        question.Answer === answerValue ||
                        question.Answer === answer.Answer;

                      return (
                        <div
                          key={aIndex}
                          className="form-check mb-2"
                        >
                          <input
                            className="form-check-input"
                            type="radio"
                            name={`aoe_${panelId}_${testOption?.testID}_${question.QuestionId}`}
                            checked={!!isSelected}
                            onChange={() =>
                              handleAOEAnswerChange(
                                panelId,
                                testOption?.testID,
                                question.QuestionId,
                                answer.AnswerId,
                                answerValue
                              )
                            }
                          />
                          <label className="form-check-label">
                            {answerLabel}
                          </label>
                        </div>
                      );
                    }
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


// Lists the member tests of a group test beneath its name.
// The list expands when the group test is selected and collapses when it is deselected.
// `indent` lines the list up with the test name when a checkbox precedes it.
const GroupTestList = ({ testOption, indent = true }: any) => {
  const groupTests: string[] = testOption?.isGroupTest
    ? (testOption?.groupTests ?? []).filter(Boolean)
    : [];
  if (!testOption?.isSelected || !groupTests.length) return null;

  return (
    <ul
      className="text-muted fs-7 mb-0 mt-1"
      style={{ paddingLeft: indent ? "44px" : "16px" }}
    >
      {groupTests.map((groupTestName: string, groupIndex: number) => (
        <li key={groupIndex}>{groupTestName}</li>
      ))}
    </ul>
  );
};

export { MultipleHeaderSelectable2 };
const SearchInput = (props: any) => {
  const { t } = useLang();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSyncingFromPropsRef = useRef(false);

  // Debounce search term for better performance
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 150); // 150ms debounce delay

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Memoize formatted options for performance
  const formattedOptions = useMemo(() => {
    return props.testOptions.map((test: any) => ({
      value: test.testID,
      label: test.testName || '',
      code: test.testCode || ''
    }));
  }, [props.testOptions]);

  // Memoize filtered options with accurate filtering and result prioritization
  const filteredOptions = useMemo(() => {
    const searchLower = debouncedSearchTerm.trim().toLowerCase();

    // Return empty array if search is empty
    if (!searchLower) {
      return [];
    }

    // Filter options that match the search term
    const filtered = formattedOptions.filter((option: any) => {
      // Safely get and normalize label and code
      const label = String(option.label || '').trim().toLowerCase();
      const code = String(option.code || '').trim().toLowerCase();

      // Case-insensitive matching for both label and code
      const labelMatch = label.includes(searchLower);
      const codeMatch = code.includes(searchLower);

      return labelMatch || codeMatch;
    });

    // Check if any option has an exact code match
    const hasExactCodeMatch = filtered.some((option: any) => {
      const code = String(option.code || '').trim().toLowerCase();
      return code === searchLower;
    });

    // If there's an exact code match, prioritize exact code matches first
    if (hasExactCodeMatch) {
      return filtered.sort((a: any, b: any) => {
        const aCode = String(a.code || '').trim().toLowerCase();
        const bCode = String(b.code || '').trim().toLowerCase();
        const aLabel = String(a.label || '').trim().toLowerCase();
        const bLabel = String(b.label || '').trim().toLowerCase();

        // Exact code match gets highest priority
        const aExactCode = aCode === searchLower;
        const bExactCode = bCode === searchLower;
        if (aExactCode && !bExactCode) return -1;
        if (!aExactCode && bExactCode) return 1;

        // Then exact label match
        const aExactLabel = aLabel === searchLower;
        const bExactLabel = bLabel === searchLower;
        if (aExactLabel && !bExactLabel) return -1;
        if (!aExactLabel && bExactLabel) return 1;

        // Then code starts with
        const aCodeStartsWith = aCode.startsWith(searchLower);
        const bCodeStartsWith = bCode.startsWith(searchLower);
        if (aCodeStartsWith && !bCodeStartsWith) return -1;
        if (!aCodeStartsWith && bCodeStartsWith) return 1;

        // Then label starts with
        const aLabelStartsWith = aLabel.startsWith(searchLower);
        const bLabelStartsWith = bLabel.startsWith(searchLower);
        if (aLabelStartsWith && !bLabelStartsWith) return -1;
        if (!aLabelStartsWith && bLabelStartsWith) return 1;

        // Then label match over code match
        const aLabelMatch = aLabel.includes(searchLower);
        const bLabelMatch = bLabel.includes(searchLower);
        if (aLabelMatch && !bLabelMatch) return -1;
        if (!aLabelMatch && bLabelMatch) return 1;

        return 0;
      });
    }

    // If no exact code match, use standard prioritization
    return filtered.sort((a: any, b: any) => {
      const aLabel = String(a.label || '').trim().toLowerCase();
      const bLabel = String(b.label || '').trim().toLowerCase();
      const aCode = String(a.code || '').trim().toLowerCase();
      const bCode = String(b.code || '').trim().toLowerCase();

      // Exact match gets highest priority
      const aExact = aLabel === searchLower || aCode === searchLower;
      const bExact = bLabel === searchLower || bCode === searchLower;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      // Then prioritize matches that start with search term
      const aStartsWith = aLabel.startsWith(searchLower) || aCode.startsWith(searchLower);
      const bStartsWith = bLabel.startsWith(searchLower) || bCode.startsWith(searchLower);
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;

      // Then prioritize label matches over code matches
      const aLabelMatch = aLabel.includes(searchLower);
      const bLabelMatch = bLabel.includes(searchLower);
      if (aLabelMatch && !bLabelMatch) return -1;
      if (!aLabelMatch && bLabelMatch) return 1;

      return 0;
    });
  }, [debouncedSearchTerm, formattedOptions]);

  const handleSelect = (option: any) => {
    isSyncingFromPropsRef.current = false;
    setSelectedOptions((prev: any) => {
      // Check if option is already selected to prevent duplicates
      const alreadySelected = prev.some(
        (item: any) => item.value === option.value
      );

      if (alreadySelected) {
        props?.setDuplicateTestError?.("");
        // Remove if already selected (toggle off)
        return prev.filter((item: any) => item.value !== option.value);
      } else {
        const duplicateSelection = props?.checkDuplicateForSearchSelection?.(
          props?.index,
          option
        );
        if (duplicateSelection) {
          props?.setDuplicateTestError?.(
            `${option?.label} is already selected in ${duplicateSelection.panelName}.`
          );
          return prev;
        }
        props?.setDuplicateTestError?.("");
        // Add if not selected, ensuring no duplicates
        return [...prev, option];
      }
    });
    setSearchTerm(""); // Clear input after selection
    setShowDropdown(false); // Hide dropdown
  };

  // Sync selectedOptions with props.selectedTestOptions (for edit mode) - prevent duplicates
  useEffect(() => {
    if (props.selectedTestOptions && Array.isArray(props.selectedTestOptions)) {
      const options = props.selectedTestOptions.map((test: any) => ({
        value: test.testID,
        label: test.testName,
        code: test.testCode
      }));

      // Remove duplicates based on value
      const uniqueOptions = options.filter((option: any, index: number, self: any[]) =>
        index === self.findIndex((o: any) => o.value === option.value)
      );

      // Only update if the options are different to prevent infinite loops
      const currentValues = selectedOptions.map((opt: any) => opt.value).sort().join(',');
      const newValues = uniqueOptions.map((opt: any) => opt.value).sort().join(',');

      if (currentValues !== newValues) {
        isSyncingFromPropsRef.current = true;
        setSelectedOptions(uniqueOptions);
      }
    }
  }, [props.selectedTestOptions]);

  // Call handleMultiSelect when selectedOptions change
  const prevSelectedOptionsRef = useRef<any[]>([]);
  useEffect(() => {
    if (isSyncingFromPropsRef.current) {
      isSyncingFromPropsRef.current = false;
      prevSelectedOptionsRef.current = selectedOptions as any[];
      return;
    }
    // Only call if selectedOptions actually changed (not just reference)
    const currentValues = selectedOptions.map((opt: any) => opt.value).sort().join(',');
    const prevValues = prevSelectedOptionsRef.current.map((opt: any) => opt.value).sort().join(',');

    if (currentValues !== prevValues) {
      props.handleMultiSelect(props?.panelName, props?.index, selectedOptions);
      prevSelectedOptionsRef.current = selectedOptions;
    }
  }, [selectedOptions, props?.panelName, props?.index]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (
        dropdownRef.current &&
        !(dropdownRef.current as any).contains(event.target)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`${props?.displayType}`} ref={dropdownRef}>
      <div className="row">
        <div className="col-lg-12">
          <div className="card border border-gray-300 mb-3 rounded">
            <div className="card-header bg-light d-flex justify-content-between align-items-center rounded min-h-35px px-4">
              <div className="col-12">
                <label className="col-12">
                  <span className="fw-600">{props?.panelName}</span>
                </label>
              </div>
            </div>
            <div className="card-body py-md-4 py-3 col-12">
              {props?.duplicateTestError && (
                <div className="form__error mb-2">
                  <span>{props?.duplicateTestError}</span>
                </div>
              )}
              {/* Search Input */}
              <input
                type="text"
                placeholder={t("Search...")}
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchTerm(value);
                  setShowDropdown(value.trim().length > 0); // Show list only when typing
                }}
                className={`form-control ${props?.disabled ? "" : "bg-transparent"
                  }`}
                autoComplete="off"
              />

              {/* Popover Dropdown */}
              {showDropdown && filteredOptions.length > 0 && (
                <div className="dropdown-menu show w-100 mt-1 shadow">
                  {filteredOptions.map((option: any) => (
                    <button
                      key={option.value}
                      className="dropdown-item d-flex justify-content-between align-items-center"
                      onClick={() => handleSelect(option)}
                    >
                      <span>
                        {option.label}
                        {option.code && (
                          <>
                            {' - '}
                            <span className="text-muted">{option.code}</span>
                          </>
                        )}
                      </span>
                      {selectedOptions.some(
                        (item: any) => item.value === option.value
                      ) && <span className="badge bg-primary">✔</span>}
                    </button>
                  ))}
                </div>
              )}

              {/* Selected Badges */}
              {selectedOptions.length > 0 && (
                <div className="mt-3">
                  {selectedOptions.map((option: any) => {
                    const testOption = props.testOptions.find((t: any) => t.testID === option.value);
                    // Create a testOption object with isSelected set to true for AOE display
                    const testOptionForAOE = testOption ? { ...testOption, isSelected: true } : null;
                    return (
                      <div key={option.value} className="mb-3">
                        <span
                          className="badge bg-primary text-white me-2 p-2"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSelect(option)}
                        >
                          ✕ {option.label}
                          {option.code && (
                            <>
                              {' - '}
                              <span style={{ opacity: 0.8 }}>{option.code}</span>
                            </>
                          )}
                        </span>
                        <GroupTestList testOption={testOptionForAOE} indent={false} />
                        {testOptionForAOE && (
                          <AOEQuestions
                            testOption={testOptionForAOE}
                            panelId={props.panelId}
                            panelName={props.panelName}
                            openAOESections={props.openAOESections}
                            toggleAOESection={props.toggleAOESection}
                            parseTestAOES={props.parseTestAOES}
                            handleAOEAnswerChange={props.handleAOEAnswerChange}
                            handleAOEMultipleSelectionChange={props.handleAOEMultipleSelectionChange}
                            AOEErrors={props.AOEErrors}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
